/**
 * Helpers for multi-table MappingWorkbench DQX: one job per region, then
 * a file-mapped event that carries all runs for board polling.
 */

export interface MappingWorkbenchDqxRun {
  regionId: string | null;
  runId: number;
  jobId: string;
  message: string | null;
  sourceRowIndexStart?: number | null;
}

export interface ApplyMappingsDqxFields {
  rows_written: number;
  dqx_run_id?: number | null;
  dqx_job_id?: string | null;
  dqx_trigger_message?: string | null;
  source_row_index_start?: number | null;
}

/** Primary `dqx` (last run) plus full `dqxRuns` for the board host. */
export function buildFileMappedDqxPayload(runs: MappingWorkbenchDqxRun[]): {
  dqx: (MappingWorkbenchDqxRun & { regionId?: string | null }) | null;
  dqxRuns: MappingWorkbenchDqxRun[];
} {
  if (runs.length === 0) {
    return { dqx: null, dqxRuns: [] };
  }
  const last = runs[runs.length - 1]!;
  return {
    dqx: {
      regionId: last.regionId,
      runId: last.runId,
      jobId: last.jobId,
      message: last.message,
      sourceRowIndexStart: last.sourceRowIndexStart ?? null,
    },
    dqxRuns: [...runs],
  };
}

/**
 * After a multi-region apply with triggerDqx, require a run id whenever rows
 * were written (same strictness as board Run DQ).
 */
/** Dedupe runs by region + job id, preserving first-seen order. */
export function mergeMappingWorkbenchDqxRuns(
  ...groups: MappingWorkbenchDqxRun[][]
): MappingWorkbenchDqxRun[] {
  const seen = new Set<string>();
  const merged: MappingWorkbenchDqxRun[] = [];
  for (const group of groups) {
    for (const run of group) {
      if (!run.jobId?.trim() || run.runId == null) continue;
      const key = `${run.regionId ?? ""}::${run.jobId}`;
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push(run);
    }
  }
  return merged;
}

export function pipelineDqxRunsToWorkbenchRuns(
  runs: {
    regionId?: string | null;
    runId: number;
    jobId: string;
    message?: string | null;
    sourceRowIndexStart?: number | null;
  }[]
): MappingWorkbenchDqxRun[] {
  return runs
    .filter((run) => Boolean(run.jobId?.trim()) && run.runId != null)
    .map((run) => ({
      regionId: run.regionId ?? null,
      runId: run.runId,
      jobId: run.jobId,
      message: run.message ?? null,
      sourceRowIndexStart: run.sourceRowIndexStart ?? null,
    }));
}

export function requireDqxRunFromApply(
  regionId: string,
  result: ApplyMappingsDqxFields
): MappingWorkbenchDqxRun {
  if (result.rows_written <= 0) {
    throw new Error(
      `No rows written for table "${regionId}"; data quality checks were not started.`
    );
  }
  const runId = result.dqx_run_id ?? null;
  if (runId == null) {
    throw new Error(
      `Data quality checks could not be started for table "${regionId}" (no run id returned).`
    );
  }
  return {
    regionId,
    runId,
    jobId: result.dqx_job_id ?? "",
    message: result.dqx_trigger_message ?? null,
    sourceRowIndexStart: result.source_row_index_start ?? null,
  };
}
